<template>
  <v-sheet class="px-3">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-row class="header-sticky">
      <v-card :color="$vuetify.theme.dark ? '#2E2E2E' : 'white'" min-width="640" class="w-100 rounded-lg settings-card" :width="'100%'" elevation="4">
        <v-card-title>
          <v-row justify="start" no-gutters>
            <v-icon large class="pr-2">mdi-cog</v-icon>
            <h3>{{ $t("settingsTitle") }}</h3>
          </v-row>
        </v-card-title>

        <v-card-text class="px-4 settings-content">
          <v-tabs v-model="activeTab" show-arrows class="mb-6" :background-color="$vuetify.theme.dark ? '#2E2E2E' : 'white'">
            <v-tab>{{ $t("settingsTabGeneral") }}</v-tab>
            <v-tab>{{ $t("settingsTabSecurity") }}</v-tab>
            <v-tab>{{ $t("settingsTabTimeManagement") }}</v-tab>
            <v-tab>{{ $t("settingsTabProjects") }}</v-tab>
            <v-tab>{{ $t("settingsTabServiceCalls") }}</v-tab>
            <v-tab>{{ $t("settingsTabBusinessTrips") }}</v-tab>
            <v-tab>{{ $t("settingsTabCalculation") }}</v-tab>
          </v-tabs>

          <v-sheet :color="$vuetify.theme.dark ? '#2E2E2E' : 'white'" elevation="0" class="pa-0">
            <v-tabs-items v-model="activeTab" class="settings-tabs">
              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('g1')" class="py-2 pt-4 align-center" @mouseenter="hoverEnter('g1')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center"> {{ "Logo" }}</v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-row no-gutters justify="end" class="align-center">
                      <input
                        class="settings-file-input"
                        type="file"
                        id="file"
                        accept="image/png, image/jpeg, image/jpg"
                        ref="file"
                        :key="refresh"
                        v-on:change="handleFileUpload()"
                      />
                      <v-tooltip top color="primary">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="primary--text" style="cursor: pointer; width: 20px; text-align: center" @click="resetLogo">
                            x
                          </div>
                        </template>
                        <span>{{ $t("resetLogo") }}</span>
                      </v-tooltip>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('g2')" class="py-2 align-center" @mouseenter="hoverEnter('g2')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center font-weight-regular">
                    {{ this.$t("messagesLanguage") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-select dense v-model="selectedLanguage" :items="languages" :label="$t('language_picker_helper')" hide-details></v-select>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('g3')" class="py-2 align-center" @mouseenter="hoverEnter('g3')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("defaultState") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-select
                      dense
                      v-model="selectedState"
                      :items="states"
                      item-text="name"
                      item-value="value"
                      :label="$t('defaultState')"
                      hide-details
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('g4')" class="py-0 align-center" @mouseenter="hoverEnter('g4')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("showCostCentre") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="showCostCentreOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="showCostCentreOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('g5')" class="py-2 align-center" @mouseenter="hoverEnter('g5')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("requireLocation") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="requireLocationOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="requireLocationOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row :style="rowStyle('g6')" class="py-2 align-center" @mouseenter="hoverEnter('g6')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">{{ this.$t("automaticLocationByRFID") }}</v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="automaticLocationByRFIDOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="automaticLocationByRFIDOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('s1')" class="py-3 align-center" @mouseenter="hoverEnter('s1')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ $t("resetAdminPassword") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-btn color="primary" class="textColor--white buttonHoverOpacity" @click="userPasswordChangeDialog = true">
                      {{ $t("resetPassword") }}
                    </v-btn>
                    <ResetPassword v-if="userPasswordChangeDialog" :newpasswordonlogin="true" :empid="'0000'" @finished="userPasswordChangeDialog = false" />
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('tm1')" class="py-2 align-center" @mouseenter="hoverEnter('tm1')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("allowUserCloseOpenBooking") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="allowUserCloseOpenBookingOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="allowUserCloseOpenBookingOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('tm2')" class="py-2 align-center" @mouseenter="hoverEnter('tm2')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("hideCommentByTimeCorrection") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="hideCommentByTimeCorrectionOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="hideCommentByTimeCorrectionOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('p1')" class="py-2 align-center" @mouseenter="hoverEnter('p1')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("stageProjectsAllowed") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="stageProjectsOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="stageProjectsOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('p2')" class="py-2 align-center" @mouseenter="hoverEnter('p2')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("stageProjectsWithoutPermissions") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="stageProjectsAllRightsOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="stageProjectsAllRightsOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('p3')" class="py-2 align-center" @mouseenter="hoverEnter('p3')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("showFinanceProjectByProjectManagement") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="showFinanceProjectByProjectManagement"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="showFinanceProjectByProjectManagement == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('p4')" class="py-2 align-center" @mouseenter="hoverEnter('p4')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("financeProjectsAllowed") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="financeProjectsOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="financeProjectsOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('p5')" class="py-2 align-center" @mouseenter="hoverEnter('p5')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("allowProjectComment") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="projectCommentOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="projectCommentOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('sc1')" class="py-2 align-center" @mouseenter="hoverEnter('sc1')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("serviceCallsAllowed") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="serviceCallsOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="serviceCallsOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('sc2')" class="py-2 align-center" @mouseenter="hoverEnter('sc2')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("serviceCallsWithoutPermissions") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="serviceCallsAllRightsOn"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="serviceCallsAllRightsOn == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item class="mx-4">
                <v-row :style="rowStyle('bt1')" class="py-2 align-center" @mouseenter="hoverEnter('bt1')" @mouseleave="hoverLeave">
                  <v-col cols="4" class="d-flex align-center">
                    {{ this.$t("showBusinessTripByTimeKeeping") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="showBusinessTripByTimeKeeping"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="showBusinessTripByTimeKeeping == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('bt2')" class="py-2 align-center" @mouseenter="hoverEnter('bt2')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ this.$t("showBusinessTripByTimeKeepingOnlyIfItsApproved") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-switch
                      dense
                      v-model="showBusinessTripByTimeKeepingOnlyIfItsApproved"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :label="showBusinessTripByTimeKeepingOnlyIfItsApproved == 1 ? $t('yes') : $t('no')"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item class="mx-4">
                <v-overlay :value="timeAccountLoader" :absolute="true">
                  <v-row justify="center">
                    <v-alert color="white" style="color: black">
                      {{ $t("timeAccountsRecalculateAlert") }}
                    </v-alert>
                  </v-row>
                </v-overlay>

                <v-row :style="rowStyle('sCalc')" class="py-3 align-center" @mouseenter="hoverEnter('sCalc')" @mouseleave="hoverLeave">
                  <v-col cols="8" class="d-flex align-center">
                    {{ $t("TimeAccountRecalculation") }}
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4" class="d-flex align-center justify-end">
                    <v-btn color="primary" class="textColor--text buttonHoverOpacity" @click="showRecalculateDialog = true">
                      {{ $t("startTimeAccountRecalculation") }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row :style="rowStyle('c2')" class="py-2 align-center" @mouseenter="hoverEnter('c2')" @mouseleave="hoverLeave">
                  <v-col cols="12" class="d-flex align-center">
                    <div class="caption orange--text mt-2">
                      {{ $t("timeAccountsWarningText") }}
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-sheet>
        </v-card-text>
        <v-card-actions class="pa-0">
          <v-row no-gutters align="center" class="w-100">
            <v-column>
              <v-row justify="start" no-gutters class="pa-0 pl-7">
                <div>COBI.time Version: {{ $version }}</div>
              </v-row>
            </v-column>
            <v-spacer></v-spacer>
            <v-column class="py-3">
              <v-row justify="end" no-gutters class="pa-0 pr-7">
                <v-btn color="primary" class="textColor--white buttonHoverOpacity" @click="saveSettings()">
                  {{ $t("save") }}
                </v-btn>
              </v-row>
            </v-column>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-row>
    <v-dialog v-model="showRecalculateDialog" width="500px">
      <v-card>
        <v-card-title>
          <div>
            <h4>{{ $t("startRecalculationRequest") }}</h4>
          </div>
        </v-card-title>
        <v-card-text>{{ $t("timeAccountsWarningGeneral") }}</v-card-text>
        <v-card-actions>
          <v-row justify="center" no-gutters>
            <v-btn color="primary darken-1" @click="recalculateTimeAccounts">
              {{ $t("confirm") }}
            </v-btn>
          </v-row>
          <v-row justify="center" no-gutters>
            <v-btn color="primary darken-1" @click="showRecalculateDialog = false">
              {{ $t("cancel") }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import imageCompression from "browser-image-compression";
import moment from "moment";

export default {
  name: "Settings",
  route: "Settings",
  components: { ResetPassword: () => import("../resetPassword") },
  data: (vm) => ({
    file: "",
    mynewlogo: "",
    fileArray: "",
    factorAuth: "",
    loaderOverlay: false,
    updateIntervalInMs: 60000,
    password: "",
    showRecalculateDialog: false,
    timeAccountLoader: false,
    backgroundWorker: "",
    showPass: false,
    allowUserCloseOpenBookingOn: 1,
    stageProjectsOn: 0,
    serviceCallsOn: 0,
    financeProjectsOn: 0,
    stageProjectsAllRightsOn: 0,
    showFinanceProjectByProjectManagement: 0,
    serviceCallsAllRightsOn: 0,
    showAbsenceApprovals: 0,
    projectCommentOn: 0,
    showCostCentreOn: 0,
    showNewTimeCorrection: 0,
    hideCommentByTimeCorrectionOn: 0,
    showBusinessTripByTimeKeeping: 0,
    showBusinessTripByTimeKeepingOnlyIfItsApproved: 0,
    selectedLanguage: "de",
    languages: ["en", "de"],
    selectedState: null,
    states: [],
    recalculateDate: moment().format("YYYY-MM-DD"),
    recalculationMenu: false,
    logoWasReset: false,
    refresh: 0,
    userPasswordChangeDialog: false,
    requireLocationOn: 0,
    automaticLocationByRFIDOn: 0,
    activeTab: 0,
    hoveredKey: null
  }),
  created: async function () {
    await this.checkIfTimeAccountRecalculationRunning();
    await this.getSettings();
    this.states = this.$store.getters["server/states"];
    this.selectedState = this.$store.getters["server/defaultState"];
    this.backgroundWorker = setInterval(this.checkIfTimeAccountRecalculationRunning, this.updateIntervalInMs);
  },
  beforeDestroy() {
    clearTimeout(this.backgroundWorker);
  },
  computed: {
    settings: function () {
      return [
        { name: "logo", description: "Logo", show: true },
        {
          name: "resetAdminPassword",
          description: this.$t("resetAdminPassword"),
          show: true
        },
        {
          name: "backendLanguage",
          description: this.$t("messagesLanguage"),
          show: true
        },
        {
          name: "defaultState",
          description: this.$t("defaultState"),
          show: true
        },
        {
          name: "accountRecalculation",
          description: this.$t("TimeAccountRecalculation"),
          show: true
        },
        {
          name: "allowUserCloseOpenBooking",
          description: this.$t("allowUserCloseOpenBooking"),
          show: true
        },
        {
          name: "stageProjects",
          description: this.$t("stageProjectsAllowed"),
          show: true
        },
        {
          name: "stageProjectsAllRights",
          description: this.$t("stageProjectsWithoutPermissions"),
          show: false
        },
        {
          name: "showFinanceProjectByProjectManagement",
          description: this.$t("showFinanceProjectByProjectManagement"),
          show: false
        },
        {
          name: "serviceCalls",
          description: this.$t("serviceCallsAllowed"),
          show: true
        },
        {
          name: "serviceCallsAllRights",
          description: this.$t("serviceCallsWithoutPermissions"),
          show: false
        },
        {
          name: "financeProjects",
          description: this.$t("financeProjectsAllowed"),
          show: true
        },
        {
          name: "projectComment",
          description: this.$t("allowProjectComment"),
          show: true
        },
        {
          name: "showCostCentre",
          description: this.$t("showCostCentre"),
          show: true
        },
        {
          name: "requireLocation",
          description: this.$t("requireLocation"),
          show: true
        },
        {
          name: "hideCommentByTimeCorrection",
          description: this.$t("hideCommentByTimeCorrection"),
          show: true
        },
        {
          name: "showBusinessTripByTimeKeeping",
          description: this.$t("showBusinessTripByTimeKeeping"),
          show: true
        },
        {
          name: "showBusinessTripByTimeKeepingOnlyIfItsApproved",
          description: this.$t("showBusinessTripByTimeKeepingOnlyIfItsApproved"),
          show: true
        }
      ];
    }
  },
  watch: {
    stageProjectsOn: function (val) {
      if (val) {
        this.settings.find((i) => i.name == "stageProjectsAllRights").show = true;
        this.settings.find((i) => i.name == "showFinanceProjectByProjectManagement").show = true;
      } else {
        this.settings.find((i) => i.name == "stageProjectsAllRights").show = false;
        this.settings.find((i) => i.name == "showFinanceProjectByProjectManagement").show = false;
      }
    },
    serviceCallsOn: function (val) {
      if (val) this.settings.find((i) => i.name == "serviceCallsAllRights").show = true;
      else this.settings.find((i) => i.name == "serviceCallsAllRights").show = false;
    }
  },
  methods: {
    hoverEnter(key) {
      this.hoveredKey = key;
    },
    hoverLeave() {
      this.hoveredKey = null;
    },
    rowStyle(key) {
      let hovered = this.hoveredKey === key;
      if (this.$vuetify.theme.dark) {
        return { backgroundColor: hovered ? "#424242" : "#2E2E2E" };
      } else {
        return { backgroundColor: hovered ? "#eeeeee" : "#ffffff" };
      }
    },

    async getSettings() {
      this.mynewlogo = this.$store.getters["server/logo"];
      this.loaderOverlay = true;
      await this.$store
        .dispatch("server/getSettings", this.$moment())
        .then(() => {
          let settings = this.$store.getters["server/factorAuth"];
          let factorAuth = settings.find((s) => s.U_settingName == "factorAuth");
          this.setSettings();
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
          }
        });
      this.fileArray = null;
      this.loaderOverlay = false;
    },
    setSettings() {
      this.allowUserCloseOpenBookingOn = this.$store.getters["server/allowUserCloseOpenBooking"];
      this.stageProjectsOn = this.$store.getters["server/stageProjectsAllowed"];
      this.serviceCallsOn = this.$store.getters["server/serviceCallsAllowed"];
      this.financeProjectsOn = this.$store.getters["server/financeProjectsAllowed"];
      this.stageProjectsAllRightsOn = this.$store.getters["server/stageProjectsWithoutPermissions"];
      this.showFinanceProjectByProjectManagement = this.$store.getters["server/showFinanceProjectByProjectManagement"];
      this.serviceCallsAllRightsOn = this.$store.getters["server/serviceCallsWithoutPermissions"];
      this.showAbsenceApprovals = this.$store.getters["server/showAbesenceApprovalsOnPresencelist"];
      this.projectCommentOn = this.$store.getters["server/allowProjectText"];
      this.showCostCentreOn = this.$store.getters["server/showCostCentre"];
      this.showNewTimeCorrection = this.$store.getters["server/showNewTimeCorrection"];
      this.hideCommentByTimeCorrectionOn = this.$store.getters["server/hideCommentByTimeCorrection"];
      this.defaultState = this.$store.getters["server/defaultState"];
      this.showBusinessTripByTimeKeeping = this.$store.getters["server/showBusinessTripByTimeKeeping"];
      this.showBusinessTripByTimeKeepingOnlyIfItsApproved = this.$store.getters["server/showBusinessTripByTimeKeepingOnlyIfItsApproved"];
      if (this.$store.getters["server/language"] != undefined) this.selectedLanguage = this.$store.getters["server/language"];
      this.requireLocationOn = this.$store.getters["server/requireLocation"];
      this.automaticLocationByRFIDOn = this.$store.getters["server/automaticLocationByRFID"];
    },
    async saveSettings() {
      let formattedSettings = this.formatSettings();
      this.loaderOverlay = true;
      if (formattedSettings[0].U_settingStringData == null) formattedSettings.shift();
      await this.$store
        .dispatch("server/saveSettings", formattedSettings)
        .then(async (res) => {
          if (res.status == 200) {
            this.$emit("settingsChanged");
            this.$eventHub.$emit("showSuccess", this.$t("settingsWasSaved"), "bottom", "right");
          }
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
          }
        });
      this.fileArray = null;
      this.loaderOverlay = false;
    },
    formatSettings() {
      return [
        { U_settingName: "Logo", U_settingStringData: this.fileArray },
        { U_settingName: "Language", U_settingStringData: this.selectedLanguage },
        { U_settingName: "defaultState", U_settingStringData: this.selectedState },
        {
          U_settingName: "allowUserCloseOpenBooking",
          U_settingNumericData: this.allowUserCloseOpenBookingOn
        },
        { U_settingName: "stagePrjAllowed", U_settingNumericData: this.stageProjectsOn },
        { U_settingName: "srcCallsAllowed", U_settingNumericData: this.serviceCallsOn },
        { U_settingName: "financePrjAllowed", U_settingNumericData: this.financeProjectsOn },
        { U_settingName: "stagePrjPermissions", U_settingNumericData: this.stageProjectsAllRightsOn },
        {
          U_settingName: "showFinPrjByPrjMgt",
          U_settingNumericData: this.showFinanceProjectByProjectManagement
        },
        { U_settingName: "srcCallsPermissions", U_settingNumericData: this.serviceCallsAllRightsOn },
        { U_settingName: "showAbsApprovals", U_settingNumericData: this.showAbsenceApprovals },
        { U_settingName: "allowProjectText", U_settingNumericData: this.projectCommentOn },
        { U_settingName: "showCostCentre", U_settingNumericData: this.showCostCentreOn },
        { U_settingName: "showNewTimeCorrection", U_settingNumericData: this.showNewTimeCorrection },
        { U_settingName: "hidCmtByTimeCorr", U_settingNumericData: this.hideCommentByTimeCorrectionOn },
        { U_settingName: "showBusinessTripByTimeKeeping", U_settingNumericData: this.showBusinessTripByTimeKeeping },
        { U_settingName: "showBusinessTripByTimeKeepingOnlyIfItsApproved", U_settingNumericData: this.showBusinessTripByTimeKeepingOnlyIfItsApproved },
        { U_settingName: "requireLocation", U_settingNumericData: this.requireLocationOn },
        { U_settingName: "automaticLocationByRFID", U_settingNumericData: this.automaticLocationByRFIDOn }
      ];
    },
    async checkIfTimeAccountRecalculationRunning() {
      await this.$store
        .dispatch("users/timeAccounts/checkIfTimeAccountRecalculationRunning")
        .then((res) => {
          this.timeAccountLoader = res.data.data;
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
          }
        });
    },
    async getCobiTimeVersion() {
      await this.$store
        .dispatch("server/getCobiTimeVersion")
        .then((res) => {})
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
          }
        });
    },
    resetLogo: async function (filename, fileArray) {
      this.loaderOverlay = true;
      this.refresh++;
      await this.$store
        .dispatch("server/resetLogo", {
          U_settingName: "Logo",
          U_settingStringData: ""
        })
        .then(() => {
          this.$eventHub.$emit("showSuccess", this.$t("settingsSaved") + "!");
          this.mynewlogo = "";
          this.$store
            .dispatch("server/getLogo")
            .then(async () => {})
            .catch((err) => {
              if (err.response.status != 200) {
                this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
              }
            });
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error);
          }
        });

      this.loaderOverlay = false;
    },
    handleFileUpload: async function () {
      this.file = this.$refs.file.files[0];
      var realFile = this.$refs.file.files[0];
      let fr = new FileReader();
      fr.addEventListener(
        "load",
        () => {
          this.fileArray = fr.result;
          this.mynewlogo = this.fileArray;
          this.logo = fr.result;
        },
        false
      );
      let options = { maxWidthOrHeight: 480, useWebWorker: true };
      let compressedFile = await imageCompression(realFile, options);
      if (this.file) {
        fr.readAsDataURL(compressedFile);
      } else {
        this.$eventHub.$emit("showError", "Es wurde kein Logo ausgewählt!");
      }
    },
    async recalculateTimeAccounts() {
      this.timeAccountLoader = true;
      this.showRecalculateDialog = false;
      await this.$store
        .dispatch("users/timeAccounts/recalculateTimeAccounts", {
          date: this.recalculateDate
        })
        .then((res) => {
          this.showRecalculateDialog = false;
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", this.$t(err.response.data.error));
            this.timeAccountLoader = false;
          }
        });
      this.timeAccountLoader = false;
    }
  }
};
</script>

<style scoped>
.v-col {
  font-weight: 600 !important;
}

.header-sticky {
  min-height: calc(100vh - 110px);
  display: flex;
}

.header-sticky > .v-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.settings-content {
  flex: 1;
}

.settings-card {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.theme--dark .settings-card {
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .settings-file-input {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .settings-file-input::file-selector-button {
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .settings-file-input::-webkit-file-upload-button {
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .settings-tabs,
.theme--dark .settings-tabs .v-window,
.theme--dark .settings-tabs .v-tabs-items,
.theme--dark .settings-tabs .v-window-item {
  background-color: #2e2e2e !important;
}

.theme--light .settings-tabs,
.theme--light .settings-tabs .v-window,
.theme--light .settings-tabs .v-tabs-items,
.theme--light .settings-tabs .v-window-item {
  background-color: #ffffff !important;
}
</style>
